import { useEffect, useState } from 'react';
import {
  ShieldCheck,
  KeyRound,
  Smartphone,
  Lock,
  Fingerprint,
  BellRing,
  Loader2,
  Monitor,
  LogOut,
  ShieldAlert,
  ShieldQuestion,
  Sparkles,
  ChevronRight,
} from 'lucide-react';
import SettingCard from '../../components/settings/SettingCard';
import SettingsRow from '../../components/settings/SettingsRow';
import Toggle from '../../components/settings/Toggle';
import { useSettingsStore } from '../../store/settingsStore';
import { aiService } from '../../services/settings/ai.service';
import type { SecurityInsight, InsightSeverity } from '../../types/settings.types';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/helpers';

const SEVERITY_STYLE: Record<InsightSeverity, { badge: string; icon: typeof ShieldAlert }> = {
  critical: { badge: 'bg-rose-100 text-rose-700', icon: ShieldAlert },
  warning: { badge: 'bg-amber-100 text-amber-700', icon: ShieldQuestion },
  info: { badge: 'bg-blue-100 text-blue-700', icon: ShieldCheck },
  success: { badge: 'bg-green-100 text-green-700', icon: ShieldCheck },
};

function daysAgo(iso: string) {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  return d <= 0 ? 'today' : d === 1 ? '1 day ago' : `${d} days ago`;
}

export default function Security() {
  const { data, loading, saveSecurity, revokeSession } = useSettingsStore();
  const [insights, setInsights] = useState<SecurityInsight[]>([]);
  const [analyzing, setAnalyzing] = useState(true);

  useEffect(() => {
    if (!data) return;
    let active = true;
    // `analyzing` starts true and is reset when the analysis resolves. State is
    // only updated from the async callback, never synchronously in the effect body.
    aiService.analyzeSecurity(data).then((res) => {
      if (!active) return;
      setInsights(res);
      setAnalyzing(false);
    });
    return () => {
      active = false;
    };
  }, [data]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-400 dark:text-neutral-500">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const sec = data.security;
  const score = aiService.securityScore(data);
  const scoreColor = score >= 80 ? 'text-green-600' : score >= 55 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400';

  return (
    <div className="space-y-6">
      <div className="hidden lg:block">
        <h1 className="text-2xl font-bold tracking-tight">Security</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Protect your account and review activity</p>
      </div>

      {/* Security score */}
      <SettingCard>
        <div className="flex items-center gap-5">
          <div className="relative w-20 h-20 shrink-0">
            <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="#f5f5f5" strokeWidth="3" />
              <circle
                cx="18"
                cy="18"
                r="15.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${(score / 100) * 97.4} 97.4`}
                className={scoreColor}
              />
            </svg>
            <span className={cn('absolute inset-0 flex items-center justify-center text-xl font-bold', scoreColor)}>{score}</span>
          </div>
          <div>
            <h2 className="font-bold">Security score</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {score >= 80 ? 'Strong — your account is well protected.' : score >= 55 ? 'Fair — a few improvements recommended.' : 'At risk — action recommended below.'}
            </p>
          </div>
        </div>
      </SettingCard>

      {/* AI Security Advisor */}
      <SettingCard
        title="AI Security Advisor"
        icon={Sparkles}
        description="Personalized recommendations from your settings"
        action={analyzing ? <Loader2 className="w-4 h-4 animate-spin text-neutral-400 dark:text-neutral-500" /> : undefined}
        flush
      >
        <div className="divide-y divide-neutral-100">
          {analyzing && insights.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-neutral-400 dark:text-neutral-500">Analyzing your account…</div>
          ) : (
            insights.map((ins) => {
              const style = SEVERITY_STYLE[ins.severity];
              const Icon = style.icon;
              return (
                <div key={ins.id} className="flex items-start gap-3 px-5 py-4">
                  <div className={cn('w-9 h-9 rounded-2xl flex items-center justify-center shrink-0', style.badge)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-neutral-900 dark:text-neutral-50">{ins.title}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{ins.detail}</p>
                    {ins.actionHref && ins.actionLabel && (
                      <Link to={ins.actionHref} className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-neutral-900 dark:text-neutral-50 hover:underline">
                        {ins.actionLabel} <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </SettingCard>

      {/* Core controls */}
      <SettingCard title="Sign-in & authentication" flush>
        <SettingsRow icon={KeyRound} label="Change password" description={`Last changed ${daysAgo(sec.lastPasswordChange)}`} to="/app/settings/security/change-password" />
        <SettingsRow
          icon={Smartphone}
          label="Two-factor authentication"
          description={sec.twoFactorEnabled ? `Enabled · ${sec.twoFactorMethod === 'sms' ? 'SMS' : 'Authenticator app'}` : 'Add an extra layer of protection'}
          to="/app/settings/security/two-factor"
          trailing={
            <span className={cn('text-xs font-semibold rounded-full px-2 py-0.5', sec.twoFactorEnabled ? 'bg-green-100 text-green-700' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400')}>
              {sec.twoFactorEnabled ? 'On' : 'Off'}
            </span>
          }
        />
        <SettingsRow icon={Lock} label="App Lock" description="Require a passcode to open Messego" to="/app/settings/security/app-lock" trailing={<span className="text-xs text-neutral-500 dark:text-neutral-400">{sec.appLockEnabled ? `${sec.appLockTimeout}m` : 'Off'}</span>} />
      </SettingCard>

      <SettingCard flush>
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><Fingerprint className="w-5 h-5" /></div>
            <div>
              <p className="font-semibold text-sm">Biometric unlock</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Use Face ID / fingerprint where available</p>
            </div>
          </div>
          <Toggle checked={sec.biometricEnabled} onChange={(v) => saveSecurity({ biometricEnabled: v })} label="Biometric unlock" />
        </div>
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><BellRing className="w-5 h-5" /></div>
            <div>
              <p className="font-semibold text-sm">Login alerts</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Notify me about new sign-ins</p>
            </div>
          </div>
          <Toggle checked={sec.loginAlerts} onChange={(v) => saveSecurity({ loginAlerts: v })} label="Login alerts" />
        </div>
      </SettingCard>

      {/* Active sessions */}
      <SettingCard title="Active sessions" icon={Monitor} description="Devices currently signed in to your account" flush>
        {sec.activeSessions.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-4 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-sm truncate">{s.device}</p>
                {s.current && <span className="text-[10px] font-semibold uppercase bg-green-100 text-green-700 rounded-full px-2 py-0.5">This device</span>}
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{s.browser} · {s.location} · active {daysAgo(s.lastActive)}</p>
            </div>
            {!s.current && (
              <button
                onClick={() => revokeSession(s.id)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl px-3 py-2 transition-colors"
              >
                <LogOut className="w-4 h-4" /> Revoke
              </button>
            )}
          </div>
        ))}
      </SettingCard>
    </div>
  );
}
